import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../auth/AuthContext.js";
import { api, ApiError } from "../../../api/client.js";
import { ImageRequestBundles } from "./ImageRequestBundles.js";
import { TelegramPublicationsList, TelegramPublishButton } from "./TelegramPublish.js";
import type { ImageAttemptView, ImageBatchView, ImageBundleView, ImageFinalDecision, ImageModeConfig, ImagePersonaOption, ImageSlotView } from "../../../api/types.js";
import {
  CONTENT_MODE_LABEL,
  DISPLAY_STATUS_LABEL,
  OPERATOR_ACTION_ROLE,
  REVIEWER_ACTION_ROLE,
  describeRequestedChanges,
  displayStatusTone,
  effectiveImageRole,
  formatUsd,
  isValidSlot,
  notCountedExplanation,
  roleAtLeast,
  stageLabel,
  unappliedStages,
} from "./image-ui.js";

/** Same uniform 15s refresh cadence every active-status display in this app uses. */
const REFRESH_INTERVAL_MS = 15_000;

const PLACEHOLDER_TEXT: Record<string, string> = {
  waiting: "Waiting for its turn…",
  generating: "Generating this image…",
  verifying: "Checking identity and distinctness…",
  modifying: "Applying your requested changes…",
  retrying: "Trying again with a different variation…",
};

/** Where each area's back link goes: CREATE / IMAGE is a working area; REVIEW and HISTORY are the main system areas. */
export type ImageDetailArea = "create" | "reviews" | "history";
const BACK_LINK: Record<ImageDetailArea, { readonly to: string; readonly label: string }> = {
  create: { to: "/generate/create/image", label: "← Image mode" },
  reviews: { to: "/generate/reviews", label: "← REVIEWS" },
  history: { to: "/generate/history", label: "← HISTORY" },
};

/** The stored image as a file download (`?download=1` = the same bytes with an attachment disposition). Needs no Bundle. */
export function imageDownloadHref(imageUrl: string): string {
  return `${imageUrl}${imageUrl.includes("?") ? "&" : "?"}download=1`;
}

function DownloadLink({ imageUrl, label, testId }: { readonly imageUrl: string; readonly label: string; readonly testId: string }) {
  return (
    <a href={imageDownloadHref(imageUrl)} download data-testid={testId} aria-label={label}>
      Download
    </a>
  );
}

function AttemptDetails({ attempt, slotOfAttemptId }: { readonly attempt: ImageAttemptView; readonly slotOfAttemptId: (id: string) => number | null }) {
  const duplicateSlot = attempt.duplicateOfAttemptId !== null ? slotOfAttemptId(attempt.duplicateOfAttemptId) : null;
  return (
    <li data-testid={`image-attempt-${attempt.id}`}>
      <strong>
        Attempt {attempt.attemptNumber}
        {attempt.isManualRetry ? " (manual retry)" : ""}
      </strong>{" "}
      — {DISPLAY_STATUS_LABEL[attempt.displayStatus]}
      <div className="muted">{attempt.spec.text}</div>
      {attempt.imageUrl && (
        <div>
          <DownloadLink imageUrl={attempt.imageUrl} label={`Download attempt ${attempt.attemptNumber}`} testId={`image-attempt-download-${attempt.id}`} />
        </div>
      )}
      {attempt.spec.planNote && <div className="muted">Planner note: {attempt.spec.planNote}</div>}
      {attempt.failureCode && (
        <div className="muted">
          {attempt.failureCode}
          {attempt.failureDetail ? `: ${attempt.failureDetail}` : ""}
        </div>
      )}
      {attempt.identityVerdict && (
        <div className="muted">
          Identity: {attempt.identityVerdict}
          {attempt.identityReason ? ` — ${attempt.identityReason}` : ""}
        </div>
      )}
      {(attempt.distinctnessVerdict || attempt.distinctnessReason) && (
        <div className="muted">
          Distinctness: {attempt.distinctnessVerdict ?? "n/a"}
          {attempt.distinctnessReason ? ` — ${attempt.distinctnessReason}` : ""}
          {duplicateSlot !== null ? ` (closest: slot ${duplicateSlot})` : ""}
        </div>
      )}
      <div className="muted">
        Cost — generation {attempt.costs.generationUsd === null ? "unknown" : formatUsd(attempt.costs.generationUsd)}, identity {attempt.costs.identityUsd === null ? "n/a" : formatUsd(attempt.costs.identityUsd)}, similarity{" "}
        {attempt.costs.distinctnessUsd === null ? "n/a" : formatUsd(attempt.costs.distinctnessUsd)}
        {attempt.costs.modificationUsd !== undefined && attempt.costs.modificationUsd !== null ? `, requested changes ${formatUsd(attempt.costs.modificationUsd)}` : ""}
      </div>
      {(attempt.modificationStages ?? []).map((stage) => (
        <div key={stage.order} className="muted" data-testid={`image-stage-${attempt.attemptNumber}-${stage.order}`}>
          Requested change {stage.order}: {stageLabel(stage.targetId)} — {stage.status}
          {stage.costUsd !== null ? ` (${formatUsd(stage.costUsd)})` : ""}
          {stage.warnings.map((warning) => (
            <div key={warning}>⚠ {warning}</div>
          ))}
        </div>
      ))}
    </li>
  );
}

/**
 * The Image batch review gallery: every slot independently understandable
 * (generating / retrying / ready / failed / moderation / identity / too
 * similar / approved / rejected / could not be produced), with per-image
 * approve/reject, optional multi-select Bundle, and a paid, explicit,
 * one-attempt Retry on exhausted slots. State is server-derived and durable
 * — a refresh, or coming back tomorrow, shows exactly the same thing.
 */
export function ImageBatchDetail({ tenantId, area = "create" }: { readonly tenantId: string; readonly area?: ImageDetailArea }) {
  const { batchId = "" } = useParams();
  const navigate = useNavigate();
  const { csrfToken, user, memberships } = useAuth();
  const role = effectiveImageRole({ isPlatformOwner: user?.isPlatformOwner === true, memberships, tenantId });
  const canOperate = roleAtLeast(role, OPERATOR_ACTION_ROLE);
  const canReview = roleAtLeast(role, REVIEWER_ACTION_ROLE);
  // FIX-067: publishing to the channels is for the platform owner only (the server enforces the same).
  const canPublishToTelegram = user?.isPlatformOwner === true;

  const [batch, setBatch] = useState<ImageBatchView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [personaName, setPersonaName] = useState<string | null>(null);
  const [pricePerImage, setPricePerImage] = useState<number | null>(null);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(new Set());
  const [retryConfirmSlot, setRetryConfirmSlot] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [finalConfirm, setFinalConfirm] = useState<ImageFinalDecision | null>(null);
  /** FIX-066: the "Stop request" confirmation is open. */
  const [stopConfirm, setStopConfirm] = useState(false);
  /** Bumped after a Bundle is saved from this page so the HISTORY "Bundles" section reloads. */
  const [bundlesVersion, setBundlesVersion] = useState(0);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const result = await api.get<{ batch: ImageBatchView }>(`/images/${encodeURIComponent(tenantId)}/batches/${encodeURIComponent(batchId)}`);
      setBatch(result.batch);
      // A transient failure must not leave the page stuck on its red banner: the next successful refresh clears it.
      setError(null);
    } catch (loadError) {
      setError(loadError instanceof ApiError ? loadError.message : "Failed to load this Image batch.");
    }
  }, [tenantId, batchId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const isTerminal = batch?.isTerminal === true;
  useEffect(() => {
    if (isTerminal) return;
    const interval = window.setInterval(() => void refresh(), REFRESH_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [isTerminal, refresh]);

  useEffect(() => {
    let cancelled = false;
    api
      .get<ImageModeConfig>(`/images/${encodeURIComponent(tenantId)}/config`)
      .then((config) => {
        if (!cancelled) setPricePerImage(config.estimatedGenerationPricePerImageUsd);
      })
      .catch(() => undefined);
    api
      .get<{ personas: ImagePersonaOption[] }>(`/images/${encodeURIComponent(tenantId)}/personas`)
      .then((result) => {
        if (!cancelled) setPersonaName(result.personas.find((p) => p.personaId === batch?.personaId)?.displayName ?? null);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [tenantId, batch?.personaId]);

  const slotOfAttemptId = useCallback(
    (attemptId: string): number | null => batch?.slots.find((s) => s.attempts.some((a) => a.id === attemptId))?.slotNumber ?? null,
    [batch],
  );

  /** Valid AND approved images — the only ones that may be selected for a Bundle (rejected or not-yet-reviewed images are ineligible; the server enforces the same rule). */
  const eligibleAttemptIds = useMemo(
    () => new Set((batch?.slots ?? []).filter(isValidSlot).flatMap((s) => (s.currentAttempt !== null && s.currentAttempt.approvalState === "approved" ? [s.currentAttempt.id] : []))),
    [batch],
  );
  const effectiveSelected = useMemo(() => [...selected].filter((id) => eligibleAttemptIds.has(id)), [selected, eligibleAttemptIds]);

  async function runAction(action: () => Promise<void>): Promise<void> {
    setBusy(true);
    setActionError(null);
    setNotice(null);
    try {
      await action();
    } catch (actionFailure) {
      setActionError(actionFailure instanceof ApiError ? actionFailure.message : "That action failed.");
    } finally {
      setBusy(false);
    }
  }

  const decide = (attemptId: string, decision: "approved" | "rejected" | "cleared") =>
    runAction(async () => {
      const result = await api.post<{ batch: ImageBatchView }>(`/images/${encodeURIComponent(tenantId)}/batches/${encodeURIComponent(batchId)}/attempts/${encodeURIComponent(attemptId)}/decision`, csrfToken, { decision });
      setBatch(result.batch);
    });

  const retrySlot = (slotNumber: number) =>
    runAction(async () => {
      const result = await api.post<{ batch: ImageBatchView }>(`/images/${encodeURIComponent(tenantId)}/batches/${encodeURIComponent(batchId)}/slots/${slotNumber}/retry`, csrfToken, { acknowledgePaidRetry: true });
      setBatch(result.batch);
      setRetryConfirmSlot(null);
      setNotice(`Slot ${slotNumber}: one additional generation attempt was started.`);
    });

  /** The request-level FINAL decision: the ONLY thing that moves a finished Image request from REVIEW to HISTORY. */
  const decideRequest = (decision: ImageFinalDecision) =>
    runAction(async () => {
      const result = await api.post<{ batch: ImageBatchView }>(`/images/${encodeURIComponent(tenantId)}/batches/${encodeURIComponent(batchId)}/final-decision`, csrfToken, { decision });
      setBatch(result.batch);
      setFinalConfirm(null);
      navigate(`/generate/history/image/${encodeURIComponent(batchId)}`);
    });

  /** FIX-066: stop a running request - nothing new is started; an image already being made finishes but is not delivered. */
  const stopRequest = () =>
    runAction(async () => {
      const result = await api.post<{ batch: ImageBatchView }>(`/images/${encodeURIComponent(tenantId)}/batches/${encodeURIComponent(batchId)}/stop`, csrfToken, {});
      setBatch(result.batch);
      setStopConfirm(false);
      setNotice(result.batch.isTerminal ? "Request stopped." : "Stopping - no new images are started. The request finishes once the current step ends.");
    });

  const createBundle = () =>
    runAction(async () => {
      const result = await api.post<{ bundle: ImageBundleView }>(`/images/${encodeURIComponent(tenantId)}/bundles`, csrfToken, { batchId, attemptIds: effectiveSelected });
      setSelected(new Set());
      setBundlesVersion((version) => version + 1);
      setNotice(`Bundle created with ${result.bundle.memberAttemptIds.length} image${result.bundle.memberAttemptIds.length === 1 ? "" : "s"}.`);
    });

  function toggleSelected(attemptId: string): void {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(attemptId)) next.delete(attemptId);
      else next.add(attemptId);
      return next;
    });
  }

  function toggleExpanded(slotNumber: number): void {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(slotNumber)) next.delete(slotNumber);
      else next.add(slotNumber);
      return next;
    });
  }

  if (batch === null) {
    return error !== null ? <div className="error-banner">{error}</div> : <div className="empty-state">Loading…</div>;
  }

  const cost = batch.trackedCost;
  return (
    <div>
      <p>
        <Link to={BACK_LINK[area].to}>{BACK_LINK[area].label}</Link>
      </p>
      {error && <div className="error-banner">{error}</div>}
      {actionError && <div className="error-banner">{actionError}</div>}
      {notice && (
        <div className="card" data-testid="image-notice">
          {notice}
        </div>
      )}

      <div className="card">
        <h3>Image batch</h3>
        <p data-testid="image-completion-message">
          <strong>{batch.completionMessage}</strong> <span className={`badge status-${batch.status}`}>{batch.status.replace(/_/g, " ")}</span>
        </p>
        <div className="kv">
          <dt>Persona</dt>
          <dd>{personaName ?? batch.personaId}</dd>
          <dt>Content mode</dt>
          <dd data-testid="image-batch-content-mode">{CONTENT_MODE_LABEL[batch.contentMode ?? "sfw"]}</dd>
          <dt>Requested changes</dt>
          <dd data-testid="image-batch-requested-changes">{describeRequestedChanges(batch.requestedChanges)}</dd>
          <dt>Requested</dt>
          <dd>
            {batch.requestedCount} image{batch.requestedCount === 1 ? "" : "s"}
          </dd>
          <dt>Estimated generation cost</dt>
          <dd data-testid="image-batch-estimate">{formatUsd(batch.estimatedGenerationCostUsd)} (initial estimate, not a total)</dd>
          <dt>Tracked cost so far</dt>
          <dd data-testid="image-batch-tracked-cost">
            <strong>{formatUsd(cost.totalUsd)}</strong> — generation {formatUsd(cost.generationUsd)}, identity checks {formatUsd(cost.identityUsd)}, similarity checks {formatUsd(cost.distinctnessUsd)}, scene analysis{" "}
            {formatUsd(cost.sceneAnalysisUsd)}
            {cost.modificationUsd !== undefined ? `, requested changes ${formatUsd(cost.modificationUsd)}` : ""}
            {cost.partial ? " (at least one call's cost is unknown, so this may understate the real spend)" : ""}
          </dd>
          <dt>Attempts per image</dt>
          <dd>up to {batch.maxAttemptsPerSlot}</dd>
          {batch.sceneSummary && (
            <>
              <dt>Scenario</dt>
              <dd>
                {batch.sceneSummary.environment}; {batch.sceneSummary.lightingMood}; {batch.sceneSummary.outfit}
              </dd>
            </>
          )}
        </div>
        {batch.failureCode !== null && batch.stopRequested !== true && (
          <p className="error-banner" data-testid="image-batch-failure">
            {batch.failureDetail ?? batch.failureCode}
          </p>
        )}
        {batch.stopRequested === true && (
          <p className="muted" data-testid="image-batch-stopped">
            {isTerminal ? "You stopped this request. Images that were already delivered are kept; you can still approve or reject the request." : "Stopping… no new images are started. An image that was already being made finishes, but it is not delivered."}
          </p>
        )}
        {!isTerminal && <p className="muted">This page refreshes automatically. You can close it — generation continues in the cloud.</p>}
        {batch.canStop === true && canReview && (
          <div data-testid="image-stop">
            {!stopConfirm ? (
              <button disabled={busy} onClick={() => setStopConfirm(true)} data-testid="image-stop-button">
                Stop request
              </button>
            ) : (
              <div data-testid="image-stop-confirm">
                <p>
                  <strong>Stop this request?</strong> No new images will be started and images that are not finished will not be delivered. Images already
                  delivered are kept, and you can then approve or reject the request. This cannot be undone.
                </p>
                <button className="primary" disabled={busy} onClick={() => void stopRequest()} data-testid="image-stop-confirm-yes">
                  Yes, stop request
                </button>{" "}
                <button disabled={busy} onClick={() => setStopConfirm(false)}>
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="card" data-testid="image-final-review">
        <h3>Final review</h3>
        {batch.finalDecision !== null ? (
          <p data-testid="image-final-outcome">
            <span className={`badge status-${batch.finalDecision}`}>{batch.finalDecision === "approved" ? "Approved" : "Rejected"}</span>{" "}
            {batch.finalDecidedAt !== null ? <span className="muted">on {new Date(batch.finalDecidedAt).toLocaleString()} — </span> : null}
            <span className="muted">this request is in HISTORY.</span>
          </p>
        ) : batch.historyOutcome === "no_image_delivered" ? (
          <p data-testid="image-final-none-delivered">
            <span className="badge">No image delivered</span> <span className="muted">Nothing was produced that could be reviewed, so there is nothing to decide. This request is in HISTORY.</span>
          </p>
        ) : !batch.awaitingFinalDecision ? (
          <p className="muted" data-testid="image-final-pending">
            This request stays in REVIEW while its images are being generated. You can make the final decision once generation has finished.
          </p>
        ) : !canReview ? (
          <p className="muted" data-testid="image-final-pending">
            Generation has finished. This request stays in REVIEW until a reviewer approves or rejects it.
          </p>
        ) : (
          <div>
            <p className="muted">
              {batch.historyOutcome === "no_decision_recorded"
                ? "This request was moved to HISTORY without a decision (\"No decision recorded\"). You can still approve or reject it as a whole."
                : "Generation has finished. The request stays in REVIEW until you approve or reject it as a whole; then it moves to HISTORY."}
              {batch.deliveredCount === 0 ? " No image was delivered, so it can only be rejected." : ""}
            </p>
            {finalConfirm === null ? (
              <>
                <button className="primary" disabled={busy || batch.deliveredCount === 0} onClick={() => setFinalConfirm("approved")} data-testid="image-final-approve">
                  Approve request
                </button>{" "}
                <button disabled={busy} onClick={() => setFinalConfirm("rejected")} data-testid="image-final-reject">
                  Reject request
                </button>
              </>
            ) : (
              <div data-testid="image-final-confirm">
                <p>
                  <strong>{finalConfirm === "approved" ? "Approve" : "Reject"} this whole request?</strong> It will move to HISTORY and this decision is final.
                </p>
                <button className="primary" disabled={busy} onClick={() => void decideRequest(finalConfirm)} data-testid="image-final-confirm-yes">
                  Yes, {finalConfirm === "approved" ? "approve" : "reject"} request
                </button>{" "}
                <button disabled={busy} onClick={() => setFinalConfirm(null)}>
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {canReview && (
        <div className="card" data-testid="image-bundle-bar">
          <strong>{effectiveSelected.length}</strong> image{effectiveSelected.length === 1 ? "" : "s"} selected for a Bundle.{" "}
          <button className="primary" disabled={busy || effectiveSelected.length === 0} onClick={() => void createBundle()}>
            Create Bundle
          </button>
          <p className="muted" style={{ marginTop: 8 }}>
            Bundles are optional — nothing is bundled automatically. Only images you have approved can be added, and a bundled image cannot be rejected.
          </p>
        </div>
      )}

      {/* FIX-067: owner only. A selection publishes the selected approved images; otherwise every approved image of this request. */}
      {canPublishToTelegram && eligibleAttemptIds.size > 0 && (
        <div className="card" data-testid="telegram-publish-bar">
          {effectiveSelected.length > 0 ? (
            <TelegramPublishButton tenantId={tenantId} sourceKind="attempts" sourceId={batch.id} attemptIds={effectiveSelected} csrfToken={csrfToken} label={`Publish ${effectiveSelected.length} selected to Telegram`} />
          ) : (
            <TelegramPublishButton tenantId={tenantId} sourceKind="batch" sourceId={batch.id} csrfToken={csrfToken} label="Publish approved images to Telegram" />
          )}
          <p className="muted" style={{ marginTop: 8 }}>
            Opens a preview with a caption. Nothing is posted until you click Publish now.
          </p>
        </div>
      )}

      <div className="image-gallery" data-testid="image-gallery">
        {batch.slots.map((slot: ImageSlotView) => {
          const attempt = slot.currentAttempt;
          const valid = isValidSlot(slot);
          const label = DISPLAY_STATUS_LABEL[slot.displayStatus];
          const tone = displayStatusTone(slot.displayStatus);
          const explanation = valid ? null : notCountedExplanation(attempt?.displayStatus ?? slot.displayStatus);
          // A `change_not_delivered` warning is presented by the "requested change NOT delivered" banner below (with the reason), not repeated in this list.
          const reviewWarnings = valid ? (attempt?.reviewWarnings ?? []).filter((w) => w.kind !== "change_not_delivered") : [];
          const undelivered = valid ? (attempt?.undeliveredChanges ?? []) : [];
          return (
            <div key={slot.slotNumber} className={`image-slot-card${valid ? "" : " dim"}`} data-testid={`image-slot-${slot.slotNumber}`}>
              <div className="image-slot-header">
                <strong>Slot {slot.slotNumber}</strong>
                <span className={`badge image-tone-${tone}`} data-testid={`image-slot-${slot.slotNumber}-status`}>
                  {label}
                  {valid && slot.displayStatus === "ready_for_review" ? (undelivered.length > 0 ? " — requested change NOT delivered" : reviewWarnings.length > 0 ? " — with warnings" : "") : ""}
                </span>
              </div>
              {attempt?.imageUrl ? (
                <img src={attempt.imageUrl} alt={`Slot ${slot.slotNumber}: ${label}`} loading="lazy" />
              ) : (
                <div className="image-slot-placeholder">{PLACEHOLDER_TEXT[slot.displayStatus] ?? slot.exhaustedReason ?? label}</div>
              )}
              <div className="muted">
                Attempt {slot.attemptsUsed} of {slot.attemptBudget}
              </div>
              {attempt?.imageUrl && (
                <div className="image-slot-actions">
                  <DownloadLink imageUrl={attempt.imageUrl} label={`Download image ${slot.slotNumber}`} testId={`image-download-${slot.slotNumber}`} />
                </div>
              )}
              {explanation && <p className="muted">{explanation}</p>}
              {/* The automatic checks flagged this image: it is NOT rejected, the reviewer decides by looking at it. */}
              {reviewWarnings.length > 0 && (
                <div className="warning-banner" style={{ color: "var(--warning, #b45309)" }} role="alert" data-testid={`image-slot-${slot.slotNumber}-review-warnings`}>
                  <strong>⚠ Review carefully — the automatic checks flagged this image. The decision is yours.</strong>
                  {reviewWarnings.map((warning, index) => (
                    <div key={`${warning.check}-${index}`} data-testid={`image-slot-${slot.slotNumber}-review-warning-${index + 1}`}>
                      ⚠ {warning.kind === "unverified" ? "Not verified" : "Deviation"} ({warning.check}): {warning.text}
                    </div>
                  ))}
                </div>
              )}
              {/* Same truthful disclosure Video's Gate 1 Review shows: the requested change was NOT applied, and the image shown is the version before it. */}
              {valid &&
                unappliedStages(attempt?.modificationStages).map((stage) => (
                  <p key={stage.order} className="warning-banner" style={{ color: "var(--warning, #b45309)" }} data-testid={`image-slot-${slot.slotNumber}-unapplied-${stage.order}`}>
                    ⚠ {stage.status === "uncertain" ? `It is uncertain whether the requested change (${stageLabel(stage.targetId)}) was applied by the provider` : `The requested change (${stageLabel(stage.targetId)}) could not be applied`}
                    {stage.failureDetail ? `: ${stage.failureDetail}` : "."} The image shown is the version BEFORE that change{stage.order > 1 ? " (with the earlier changes applied)" : ""}.
                  </p>
                ))}
              {(slot.status === "exhausted" || slot.status === "needs_reconciliation") && slot.exhaustedReason && <p className="muted">{slot.exhaustedReason}</p>}

              {valid && attempt && canReview && (
                <div className="image-slot-actions">
                  <label>
                    <input type="checkbox" checked={selected.has(attempt.id)} disabled={attempt.approvalState !== "approved"} onChange={() => toggleSelected(attempt.id)} /> Select for Bundle
                  </label>
                  <div>
                    {attempt.approvalState === "approved" ? (
                      <button disabled={busy || attempt.inBundle} title={attempt.inBundle ? "In a Bundle - it cannot be un-approved" : undefined} onClick={() => void decide(attempt.id, "cleared")}>
                        Clear approval
                      </button>
                    ) : (
                      <button className="primary" disabled={busy || attempt.approvalState === "rejected"} onClick={() => void decide(attempt.id, "approved")}>
                        Approve
                      </button>
                    )}{" "}
                    {attempt.approvalState === "rejected" ? (
                      <button disabled={busy} onClick={() => void decide(attempt.id, "cleared")}>
                        Undo reject
                      </button>
                    ) : (
                      <button disabled={busy || attempt.approvalState === "approved"} onClick={() => void decide(attempt.id, "rejected")}>
                        Reject
                      </button>
                    )}
                  </div>
                </div>
              )}

              {slot.canManualRetry && canOperate && (
                <div className="image-slot-actions">
                  {retryConfirmSlot === slot.slotNumber ? (
                    <div data-testid={`image-retry-confirm-${slot.slotNumber}`}>
                      {slot.status === "needs_reconciliation" && (
                        <p data-testid={`image-reconcile-warning-${slot.slotNumber}`}>
                          The previous generation for this slot was submitted but its outcome could not be confirmed, so it may already have been billed. Check your provider billing first.
                        </p>
                      )}
                      <p>
                        <strong>Paid retry.</strong> This starts exactly one additional generation attempt for this slot
                        {pricePerImage !== null ? ` — estimated generation cost ${formatUsd(pricePerImage)}` : ""} (verification adds a little more).
                      </p>
                      <button className="primary" disabled={busy} onClick={() => void retrySlot(slot.slotNumber)}>
                        Confirm paid retry
                      </button>{" "}
                      <button disabled={busy} onClick={() => setRetryConfirmSlot(null)}>
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button disabled={busy} onClick={() => setRetryConfirmSlot(slot.slotNumber)}>
                      Retry (paid)
                    </button>
                  )}
                </div>
              )}

              <button type="button" className="link-button" onClick={() => toggleExpanded(slot.slotNumber)} aria-expanded={expanded.has(slot.slotNumber)}>
                {expanded.has(slot.slotNumber) ? "Hide attempt history" : `Attempt history (${slot.attempts.length})`}
              </button>
              {expanded.has(slot.slotNumber) && (
                <ul className="image-attempt-list">
                  {slot.attempts.map((a) => (
                    <AttemptDetails key={a.id} attempt={a} slotOfAttemptId={slotOfAttemptId} />
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      {/* HISTORY only: the Bundles saved from THIS request, next to its images (same records and the same Download all as CREATE / IMAGE). */}
      {area === "history" && <ImageRequestBundles tenantId={tenantId} batchId={batch.id} refreshKey={bundlesVersion} canPublishToTelegram={canPublishToTelegram} csrfToken={csrfToken} />}
      {canPublishToTelegram && <TelegramPublicationsList tenantId={tenantId} batchId={batch.id} title="Telegram posts from this request" allPostsHref="/generate/create/image/telegram" />}
    </div>
  );
}
